import { useState } from 'react';
import { getAuthHeaders, handleAuthError } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { displayOrderStatusToApiSlug } from '../utils/adminOrderStatusApi';
import { extractSheenBackendErrorMessageLoose } from '../../utils/apiErrorMessage';
import { tryNotifyCustomerOrderStatus } from '../utils/adminOrderCustomerNotify';

const toOrderIdString = (orderId) => {
  if (orderId == null || orderId === '') return '';
  if (typeof orderId === 'object') {
    if (orderId.$oid) return String(orderId.$oid);
    if (orderId._id != null) return toOrderIdString(orderId._id);
  }
  return String(orderId);
};

const useUpdateOrderStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const updateOrderStatus = async (orderId, status, note = '', notifyContext = null) => {
    const id = toOrderIdString(orderId);
    if (!id) {
      const err = new Error('Order ID is required');
      setError(err.message);
      throw err;
    }
    const statusVal = String(status || '').trim();
    if (!statusVal) {
      const err = new Error('Status is required');
      setError(err.message);
      throw err;
    }
    try {
      setLoading(true);
      setUpdatingOrderId(id);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();
      const backendStatus = displayOrderStatusToApiSlug(statusVal);
      const requestBody = { status: backendStatus };
      if (note) {
        requestBody.note = note;
      }

      const response = await fetch(`${apiUrl}/api/admin/orders/${encodeURIComponent(id)}/status`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
      });

      const rawText = await response.text();
      let result = {};
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        result = { error: rawText ? rawText.slice(0, 400) : '' };
      }

      if (!response.ok) {
        const errMsg =
          extractSheenBackendErrorMessageLoose(result, response.status) ||
          `HTTP ${response.status}`;
        handleAuthError(new Error(errMsg), response);
        throw new Error(errMsg);
      }

      if (result.success !== false) {
        const out = result.data ?? result.order ?? result;
        if (notifyContext && typeof notifyContext === 'object') {
          void tryNotifyCustomerOrderStatus({
            orderId: id,
            orderNumber: notifyContext.orderNumber,
            statusSlug: backendStatus,
            displayLabel: statusVal,
            userEmail: notifyContext.userEmail,
            adminNote: note || undefined,
          });
        }
        return out;
      }
      throw new Error(result.message || result.error || 'Failed to update order status');
    } catch (err) {
      const errMsg = err.message || 'Failed to update order status';
      setError(errMsg);
      throw err;
    } finally {
      setLoading(false);
      setUpdatingOrderId(null);
    }
  };

  return { updateOrderStatus, loading, error, updatingOrderId };
};

export default useUpdateOrderStatus;


